import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { EGI_APP_ID_ROUTE_PARAM } from '../../../../projects/page-framework/src/lib/constants';
import { PageTitleService } from './page-title.service';

/**
 * Geeft na elke geslaagde navigatie de paginanaam door aan PageTitleService: een
 * `title` uit de routeconfig als die er is, anders de UPAM-menunaam van de pagina
 * (`navId`), anders de appnaam. Draait ook op de loginpagina en publieke pagina's,
 * zodat er nooit een titel van een vorige pagina blijft staan (dat gebeurde toen de
 * AuthorizationGuard de titel zette).
 */
@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  constructor(
    private readonly pageTitle: PageTitleService,
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries
  ) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.pageTitle.setPage(this.buildTitle(snapshot) ?? this.navigationItemName(snapshot));
  }

  private navigationItemName(snapshot: RouterStateSnapshot): string | undefined {
    const appId = this.findAppId(snapshot.root);
    if (!appId) {
      return undefined;
    }

    const app = this.userQueries.getAvailableApps().find(a => a.id === appId);
    if (!app) {
      return undefined;
    }

    const navId = snapshot.root.queryParamMap.get('navId');
    const navigationItem = navId ? app.rootNavigationItem.asArray.find(i => i.id === navId) : undefined;
    return navigationItem?.name || app.name;
  }

  /** Het app-segment kan op elk niveau van de route zitten. */
  private findAppId(route: ActivatedRouteSnapshot | null): string | undefined {
    let appId: string | undefined;
    for (let current = route; current; current = current.firstChild) {
      appId = current.paramMap.get(EGI_APP_ID_ROUTE_PARAM) ?? appId;
    }
    return appId;
  }
}
